import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Menu, Shield } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const { user, profile, isAdmin } = useAuth();

  const role = (profile as any)?.role;
  const dashboardHref = isAdmin
    ? '/reviewer-admin'
    : role === 'business'
    ? '/business/dashboard'
    : role === 'creator' || role === 'demo'
    ? '/creator/dashboard'
    : '/';

  const navItems = [
    { label: 'Pricing', href: '/pricing' },
    { label: 'Case Studies', href: '/case-studies' },
    { label: 'Blog', href: '/blog' },
    { label: 'Docs', href: '/docs' },
    { label: 'Contact', href: '/contact' },
  ];

  return (
    <header className="sticky top-0 z-50 w-full border-b border-[#E2E8F0] bg-white backdrop-blur-xl">
      <div className="container flex h-16 max-w-screen-xl items-center">
        <div className="mr-4 hidden md:flex">
          <Link to="/" className="mr-6 flex items-center space-x-2 group">
            <div className="flex items-center space-x-2">
              <Shield className="h-7 w-7 text-emerald-400 transition-transform group-hover:scale-110" />
              <span className="hidden font-bold sm:inline-block text-xl text-[#0F172A]">
                CreatorVault
              </span>
            </div>
          </Link>
          <nav className="flex items-center gap-6 text-sm">
            {navItems.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                className="transition-premium text-[#64748B] hover:text-[#0F172A] font-medium"
              >
                {item.label}
              </Link>
            ))}
          </nav>
        </div>

        <Sheet open={isOpen} onOpenChange={setIsOpen}>
          <SheetTrigger asChild className="md:hidden">
            <Button variant="ghost" size="sm" className="mr-2 px-0 text-base hover:bg-transparent text-[#0F172A]">
              <Menu className="h-6 w-6" />
              <span className="sr-only">Toggle menu</span>
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="pr-0 bg-white border-[#E2E8F0]">
            <Link to="/" className="flex items-center space-x-2" onClick={() => setIsOpen(false)}>
              <Shield className="h-6 w-6 text-emerald-400" />
              <span className="font-bold text-[#0F172A]">CreatorVault</span>
            </Link>
            <div className="my-4 h-[calc(100vh-8rem)] pb-10 pl-6 flex flex-col">
              <div className="flex flex-col space-y-1">
                {navItems.map((item) => (
                  <Link
                    key={item.href}
                    to={item.href}
                    className="text-[#64748B] hover:text-[#0F172A] transition-premium py-3 text-base"
                    onClick={() => setIsOpen(false)}
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
              <div className="mt-6 pr-6 space-y-3">
                {user ? (
                  <Button 
                    className="w-full bg-emerald-500 hover:bg-emerald-400 text-white border-0"
                    onClick={() => { navigate(dashboardHref); setIsOpen(false); }}
                  >
                    Dashboard
                  </Button>
                ) : (
                  <>
                    <Button 
                      variant="outline"
                      className="w-full"
                      onClick={() => { navigate('/auth?mode=signin'); setIsOpen(false); }}
                    >
                      Sign In
                    </Button>
                    <Button 
                      className="w-full bg-emerald-500 hover:bg-emerald-400 text-white border-0"
                      onClick={() => { navigate('/auth'); setIsOpen(false); }}
                    >
                      Join Now
                    </Button>
                  </>
                )}
              </div>
            </div>
          </SheetContent>
        </Sheet>

        <div className="flex flex-1 items-center justify-between space-x-2 md:justify-end">
          <div className="w-full flex-1 md:w-auto md:flex-none">
            <Link to="/" className="flex items-center space-x-2 md:hidden">
              <Shield className="h-6 w-6 text-emerald-400" />
              <span className="font-bold text-[#0F172A]">CreatorVault</span>
            </Link>
          </div>
          <nav className="flex items-center gap-2">
            {user ? (
              <Button 
                variant="default"
                onClick={() => navigate(dashboardHref)}
                className="text-sm font-medium bg-emerald-500 hover:bg-emerald-400 text-white border-0"
              >
                Dashboard
              </Button>
            ) : (
              <>
                <Button 
                  variant="ghost" 
                  onClick={() => navigate('/auth?mode=signin')}
                  className="text-sm font-medium text-[#64748B] hover:text-[#0F172A] hover:bg-slate-100"
                >
                  Sign In
                </Button>
                <Button 
                  onClick={() => navigate('/auth')}
                  className="text-sm font-medium bg-emerald-500 hover:bg-emerald-400 text-white border-0 glow-emerald"
                >
                  Join Now
                </Button>
              </>
            )}
          </nav>
        </div>
      </div>
    </header>
  );
};

export default Navigation;
